/**
 * InboxDropdown - Unified notification inbox component
 * 
 * Replaces separate notification bells with a single premium Inbox entry point.
 * Features tabbed navigation across Messages, Investor, and Finance domains
 * while preserving data separation and role-based visibility.
 * 
 * @module components/inbox/InboxDropdown
 */

import { AppIcon, type IconName } from '@/components/ui/icons';
import React, { useState, useRef, useEffect, useCallback, useMemo, useId } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications, isSecurityDerivedNotificationType, type Notification, type NotificationType } from '../../features/notifications/hooks/useNotifications';
import {
  isInvestorNotificationType,
  useInvestorNotifications,
} from '@/hooks/useInvestorNotifications';
import * as capabilityChecks from '@/lib/auth/capabilities';
import { Tabs, TabsList, TabsTrigger } from '../ui/Tabs';
import { cn } from '../../lib/utils';
import { TONE_SURFACE } from '@/lib/layout/layoutTokens';
import EmptyState from '@/components/ui/EmptyState';
import { CounterBadge, SignalDot } from '@/components/ui/StatusPrimitives';
import Tooltip from '../Tooltip';
import type { InvestorNotification } from '@/types/investorNotifications';
import { resolveInvestorNotificationPath } from '@/lib/investor/routing';
import { resolveNotificationDestination } from '@/features/notifications/notificationNavigation';

// ============================================================================
// CONSTANTS
// ============================================================================

// Message notification types that require user action (critical/actionable)
const ACTIONABLE_MESSAGE_TYPES: string[] = [
  'room_invite',
  'chat_mention',
  'thread_mention',
  'call_started',
  'decision_requested',
  'decision_action_assigned',
];

const isActionableNotification = (type: string): boolean =>
  ACTIONABLE_MESSAGE_TYPES.includes(type) || isSecurityDerivedNotificationType(type);

// ============================================================================
// NOTIFICATION TYPE CONFIG (Messages)
// ============================================================================

interface NotificationTypeConfig {
  icon: IconName;
  iconColor: string;
  bgColor: string;
  label: string;
}

const MESSAGE_NOTIFICATION_CONFIG: Record<string, NotificationTypeConfig> = {
  room_invite: {
    icon: "userPlus",
    iconColor: TONE_SURFACE.primary.text,
    bgColor: TONE_SURFACE.primary.soft,
    label: 'Chat Invite',
  },
  chat_mention: {
    icon: "messageSquare",
    iconColor: TONE_SURFACE.info.text,
    bgColor: TONE_SURFACE.info.soft,
    label: 'Mentioned',
  },
  thread_mention: {
    icon: "messageSquare",
    iconColor: TONE_SURFACE.info.text,
    bgColor: TONE_SURFACE.info.soft,
    label: 'Mentioned in Thread',
  },
  reaction: {
    icon: "checkCircle",
    iconColor: TONE_SURFACE.warning.text,
    bgColor: TONE_SURFACE.warning.soft,
    label: 'Reaction',
  },
  call_started: {
    icon: "phone",
    iconColor: TONE_SURFACE.success.text,
    bgColor: TONE_SURFACE.success.soft,
    label: 'Incoming Call',
  },
  call_missed: {
    icon: "phoneMissed",
    iconColor: TONE_SURFACE.danger.text,
    bgColor: TONE_SURFACE.danger.soft,
    label: 'Missed Call',
  },
  call_reminder: {
    icon: "phone",
    iconColor: TONE_SURFACE.primary.text,
    bgColor: TONE_SURFACE.primary.soft,
    label: 'Call Reminder',
  },
  decision_requested: {
    icon: "scale",
    iconColor: TONE_SURFACE.warning.text,
    bgColor: TONE_SURFACE.warning.soft,
    label: 'Decision Requested',
  },
  decision_action_assigned: {
    icon: "clipboardCheck",
    iconColor: TONE_SURFACE.warning.text,
    bgColor: TONE_SURFACE.warning.soft,
    label: 'Decision Action Assigned',
  },
  decision_recorded: {
    icon: "badgeCheck",
    iconColor: TONE_SURFACE.success.text,
    bgColor: TONE_SURFACE.success.soft,
    label: 'Decision Recorded',
  },
};

function resolveMessageConfig(type: NotificationType): NotificationTypeConfig {
  if (MESSAGE_NOTIFICATION_CONFIG[type]) return MESSAGE_NOTIFICATION_CONFIG[type];
  if (isSecurityDerivedNotificationType(type)) {
    return {
      icon: 'shield',
      iconColor: TONE_SURFACE.warning.text,
      bgColor: TONE_SURFACE.warning.soft,
      label: type.replace(/_/g, ' ').replace(/\./g, ' '),
    };
  }
  return MESSAGE_NOTIFICATION_CONFIG.chat_mention;
}

// ============================================================================
// MESSAGE NOTIFICATION ITEM
// ============================================================================

interface MessageItemProps {
  notification: Notification;
  onClick: (n: Notification) => void;
}

const MessageItem: React.FC<MessageItemProps> = ({ notification, onClick }) => {
  const config = resolveMessageConfig(notification.type);
  const isUnread = notification.read_at === null;

  return (
    <button
      type="button"
      aria-label={`Open notification: ${notification.title || config.label}`}
      onClick={() => onClick(notification)}
      className="ax-menu-item w-full cursor-pointer rounded-none border-b border-border-subtle/50 px-4 py-3 text-left"
    >
      <div className="flex items-start gap-3">
        <div className="ax-icon-frame ax-icon-frame--neutral h-8 w-8 flex-shrink-0">
          <AppIcon name={config.icon} className={cn('w-4 h-4', config.iconColor)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-medium text-text-primary text-sm">
              {notification.title || config.label}
            </p>
            {isUnread && (
              <SignalDot tone="info" label="Unread" />
            )}
          </div>
          <p className="text-sm text-text-secondary mt-0.5 line-clamp-2">
            {notification.preview || notification.body || 'New notification'}
          </p>
          <div className="flex items-center mt-1.5 text-xs text-text-muted">
            <AppIcon name="clock" className="w-3 h-3 mr-1" />
            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
          </div>
        </div>
      </div>
    </button>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface InboxDropdownProps {
  className?: string;
}

type InboxTab = 'messages' | 'investor';
const CAPITAL_TAB_VALUE = 'investor';
const CAPITAL_USER_CHECK = 'isInvestorUser';
const isCapitalPersonaUser = capabilityChecks[CAPITAL_USER_CHECK];

export const InboxDropdown: React.FC<InboxDropdownProps> = ({ className }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const isInvestorSurface = location.pathname === '/investor'
    || location.pathname.startsWith('/investor/');
  const isInvestorViewer = isInvestorSurface || isCapitalPersonaUser(user);
  const messagesPath = isInvestorSurface ? '/investor/chat' : '/messages';
  const [activeTab, setActiveTab] = useState<InboxTab>(isInvestorViewer ? CAPITAL_TAB_VALUE : 'messages');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dialogTitleId = useId();

  const closeAndRestoreFocus = useCallback(() => {
    setIsOpen(false);
    buttonRef.current?.focus();
  }, []);

  // Hooks for notifications
  const {
    notifications: allPersistedNotifications,
    loading: messageLoading,
    error: messageError,
    markAsRead: markMessageAsRead,
  } = useNotifications(30);

  const messageNotifications = useMemo(
    () => allPersistedNotifications.filter(
      notification => !isInvestorNotificationType(notification.type),
    ),
    [allPersistedNotifications],
  );
  const messageUnreadCount = useMemo(
    () => messageNotifications.filter(notification => notification.read_at === null).length,
    [messageNotifications],
  );

  const {
    notifications: investorNotifications,
    unreadCount: investorUnreadCount,
    markAsRead: markInvestorAsRead,
    markAllAsRead: markAllInvestorAsRead,
  } = useInvestorNotifications(isInvestorViewer ? user?.id : undefined);

  // Compute critical counts
  const messageCriticalCount = useMemo(() => {
    return messageNotifications.filter(
      n => n.read_at === null && isActionableNotification(n.type)
    ).length;
  }, [messageNotifications]);

  const investorCriticalCount = useMemo(() => {
    if (!isInvestorViewer) return 0;
    return investorNotifications.filter(
      n => !n.read && (n.severity === 'critical' || n.severity === 'warning')
    ).length;
  }, [investorNotifications, isInvestorViewer]);

  const totalCriticalCount = messageCriticalCount + investorCriticalCount;
  const hasAnyUnread = messageUnreadCount > 0 || investorUnreadCount > 0;

  // Sort messages: actionable first, then by date
  const sortedMessages = useMemo(() => {
    return [...messageNotifications].sort((a, b) => {
      const aActionable = isActionableNotification(a.type) ? 1 : 0;
      const bActionable = isActionableNotification(b.type) ? 1 : 0;
      if (aActionable !== bActionable) return bActionable - aActionable;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [messageNotifications]);

  const sortedInvestorNotifications = useMemo(() => {
    const severityWeight = (severity: 'info' | 'success' | 'warning' | 'critical') => {
      switch (severity) {
        case 'critical': return 3;
        case 'warning': return 2;
        case 'success': return 1;
        default: return 0;
      }
    };

    return [...investorNotifications].sort((a, b) => {
      if (a.read !== b.read) return a.read ? 1 : -1;
      const severityDiff = severityWeight(b.severity) - severityWeight(a.severity);
      if (severityDiff !== 0) return severityDiff;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [investorNotifications]);

  // Default tab logic
  useEffect(() => {
    if (isOpen) {
      if (isInvestorViewer && (investorCriticalCount > 0 || (investorUnreadCount > 0 && messageUnreadCount === 0))) {
        setActiveTab('investor');
      } else {
        setActiveTab('messages');
      }
    }
  }, [isOpen, isInvestorViewer, investorCriticalCount, investorUnreadCount, messageUnreadCount]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Move keyboard focus into the popover when it opens. The trigger remains the
  // stable return target for Escape and the explicit close control.
  useEffect(() => {
    if (isOpen) dropdownRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setNavigationError(null);
  }, [isOpen]);

  // Keyboard shortcut: g then n to open inbox
  useEffect(() => {
    let gPressed = false;
    let gTimeout: NodeJS.Timeout;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // ESC closes the dropdown
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        closeAndRestoreFocus();
        return;
      }

      // g + n shortcut
      if (e.key === 'g') {
        gPressed = true;
        gTimeout = setTimeout(() => {
          gPressed = false;
        }, 800);
      } else if (e.key === 'n' && gPressed) {
        e.preventDefault();
        setIsOpen(prev => !prev);
        gPressed = false;
        clearTimeout(gTimeout);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearTimeout(gTimeout);
    };
  }, [closeAndRestoreFocus, isOpen]);

  // Handle message notification click
  const handleMessageClick = useCallback(
    async (notification: Notification) => {
      setNavigationError(null);

      if (isSecurityDerivedNotificationType(notification.type) || notification.source_security_event_id) {
        await markMessageAsRead(notification.id);
        navigate(isInvestorSurface ? '/investor/settings?tab=trust' : '/security?tab=activity');
        setIsOpen(false);
        return;
      }

      const destination = await resolveNotificationDestination(notification, messagesPath);
      if (!destination) {
        setNavigationError('The linked conversation is unavailable or you no longer have access. This notification remains unread.');
        return;
      }
      await markMessageAsRead(notification.id);
      navigate(destination);
      setIsOpen(false);
    },
    [isInvestorSurface, markMessageAsRead, messagesPath, navigate]
  );

  const handleInvestorNotificationClick = useCallback((notification: InvestorNotification) => {
    if (!notification.read) {
      markInvestorAsRead(notification.id);
    }

    const destination = resolveInvestorNotificationPath(notification);
    if (destination) {
      navigate(destination);
      setIsOpen(false);
    }
  }, [markInvestorAsRead, navigate]);

  // Mark all as read for current tab
  const handleMarkAllAsRead = useCallback(async () => {
    if (activeTab === 'messages') {
      await Promise.all(
        messageNotifications
          .filter(notification => notification.read_at === null)
          .map(notification => markMessageAsRead(notification.id)),
      );
    } else if (activeTab === 'investor') {
      await markAllInvestorAsRead();
    }
  }, [activeTab, markAllInvestorAsRead, markMessageAsRead, messageNotifications]);

  return (
    <div className={cn('relative', className)}>
      {/* Inbox Button */}
      <Tooltip content="Inbox (g then n)">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="ax-icon-button relative h-11 w-11"
          aria-label={totalCriticalCount > 0 ? `Open inbox, ${totalCriticalCount} urgent` : hasAnyUnread ? 'Open inbox, unread items' : 'Open inbox'}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          <AppIcon name="bell" className="w-5 h-5" />

          {/* Badge logic: critical count > 0 = red badge, else unread = dot */}
          {totalCriticalCount > 0 ? (
            <CounterBadge
              critical
              value={totalCriticalCount > 99 ? '99+' : totalCriticalCount}
              className="absolute -right-1 -top-1"
              aria-hidden="true"
            />
          ) : hasAnyUnread ? (
            <SignalDot tone="info" className="absolute right-1 top-1" aria-hidden="true" />
          ) : null}
        </button>
      </Tooltip>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="ax-menu-surface absolute right-0 top-full z-50 mt-2 flex max-h-[calc(100dvh-5rem)] w-[calc(100vw-1.5rem)] max-w-96 flex-col overflow-hidden"
            role="dialog"
            aria-labelledby={dialogTitleId}
            tabIndex={-1}
          >
            {/* Header */}
            <div className="shrink-0 px-4 py-3 border-b border-border-subtle/50">
              <div className="flex items-center justify-between">
                <h3 id={dialogTitleId} className="font-semibold text-text-primary">Inbox</h3>
                <div className="flex items-center gap-2">
                  {((activeTab === 'messages' && messageUnreadCount > 0) ||
                    (activeTab === 'investor' && investorUnreadCount > 0)) && (
                      <button
                        type="button"
                        onClick={handleMarkAllAsRead}
                        className="min-h-11 px-2 text-xs text-text-muted transition-colors hover:text-text-secondary"
                      >
                        Mark all read
                      </button>
                    )}
                  <button
                    type="button"
                    onClick={closeAndRestoreFocus}
                    className="ax-icon-button h-11 w-11 hover:bg-surface-muted"
                    aria-label="Close inbox"
                  >
                    <AppIcon name="x" className="w-4 h-4 text-text-muted" />
                  </button>
                </div>
              </div>

              {/* Segmented Tabs */}
              <div className="mt-3">
                <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as InboxTab)}>
                  <TabsList className="w-full" role="tablist">
                    <TabsTrigger value="messages" className="min-h-11 flex-1 gap-1.5" role="tab" aria-selected={activeTab === 'messages'}>
                      <AppIcon name="messageSquare" className="w-3.5 h-3.5" />
                      Messages
                      {messageUnreadCount > 0 && (
                        <CounterBadge value={messageUnreadCount} className="ml-1" />
                      )}
                    </TabsTrigger>
                    {isInvestorViewer && (
                      <TabsTrigger value={CAPITAL_TAB_VALUE} className="min-h-11 flex-1 gap-1.5" role="tab" aria-selected={activeTab === CAPITAL_TAB_VALUE}>
                        <AppIcon name="shield" className="w-3.5 h-3.5" />
                        Notifications
                        {investorUnreadCount > 0 && (
                          <CounterBadge critical={investorCriticalCount > 0} value={investorUnreadCount} className="ml-1" />
                        )}
                      </TabsTrigger>
                    )}

                  </TabsList>
                </Tabs>
              </div>


            </div>

            {/* Content */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {navigationError && activeTab === 'messages' && (
                <p role="alert" className="border-b border-border-subtle bg-status-warning-soft px-4 py-3 text-xs leading-5 text-status-warning-foreground">
                  {navigationError}
                </p>
              )}
              {/* Messages Tab Content */}
              {activeTab === 'messages' && (
                <>
                  {messageLoading ? (
                    <div role="status" aria-live="polite" className="flex items-center justify-center py-8 gap-2 text-sm text-text-muted">
                      <AppIcon name="loader2" className="w-4 h-4 animate-spin" />
                      Loading…
                    </div>
                  ) : messageError ? (
                    <div role="alert" className="py-12 text-center px-4">
                      <AppIcon name="alertTriangle" className="w-10 h-10 mx-auto text-status-danger-foreground mb-3" />
                      <p className="text-sm font-medium text-status-danger-foreground">Notifications unavailable</p>
                      <p className="text-xs text-text-muted mt-1">{messageError}</p>
                    </div>
                  ) : sortedMessages.length === 0 ? (
                    <EmptyState
                      size="compact"
                      icon="notification"
                      title="You're all caught up"
                      description="No new messages or security alerts"
                    />
                  ) : (
                    <div className="divide-y divide-border-subtle/50">
                      {sortedMessages.map((notification) => (
                        <MessageItem
                          key={notification.id}
                          notification={notification}
                          onClick={handleMessageClick}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Investor Tab Content */}
              {activeTab === 'investor' && isInvestorViewer && (
                <>
                  {sortedInvestorNotifications.length === 0 ? (
                    <EmptyState
                      size="compact"
                      icon="security"
                      title="No investor updates right now"
                      description="Capital, milestone, and portfolio alerts will appear here"
                    />
                  ) : (
                    <div className="divide-y divide-border-subtle/50">
                      {sortedInvestorNotifications.map((notification) => {
                        const isUnread = !notification.read;
                        const isCritical = notification.severity === 'critical' || notification.severity === 'warning';

                        return (
                           
                          <button
                            key={notification.id}
                            type="button"
                            aria-label={notification.title}
                            onClick={() => handleInvestorNotificationClick(notification)}
                            className={cn(
                              'w-full text-left px-4 py-3 cursor-pointer transition-colors',
                              isUnread ? TONE_SURFACE.info.soft : '',
                              'hover:bg-surface-muted/50'
                            )}
                          >
                            <div className="flex items-start gap-3">
                              <div className={cn(
                                'p-2 rounded-xl flex-shrink-0',
                                isCritical ? 'bg-status-warning-soft' : 'bg-status-info-soft'
                              )}>
                                <AppIcon
                                  name={isCritical ? 'alertTriangle' : 'shield'}
                                  className={cn('w-4 h-4', isCritical ? 'text-status-warning-foreground' : 'text-status-info-foreground')}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="font-medium text-text-primary text-sm">
                                    {notification.title}
                                  </p>
                                  {isUnread && <div className="w-2 h-2 bg-primary-600 rounded-full flex-shrink-0" />}
                                </div>
                                <p className="text-sm text-text-secondary mt-0.5 line-clamp-2">
                                  {notification.message}
                                </p>
                                <div className="flex items-center justify-between mt-1.5 gap-2 text-xs text-text-muted">
                                  <span className="truncate">{notification.projectName || 'Investor update'}</span>
                                  <span className="whitespace-nowrap">
                                    {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}


            </div>

            {/* Footer */}
            <div className="shrink-0 px-4 py-3 border-t border-border-subtle/50 bg-surface-muted/50">
              {activeTab === 'messages' ? (
                <button
                  type="button"
                  onClick={() => {
                    navigate(messagesPath);
                    setIsOpen(false);
                  }}
                  className="min-h-11 w-full text-center text-sm font-medium text-accent-primary transition-colors hover:text-accent-primary/80"
                >
                  View All Messages
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    navigate('/investor/notifications');
                    setIsOpen(false);
                  }}
                  className="min-h-11 w-full text-center text-sm font-medium text-accent-primary transition-colors hover:text-accent-primary/80"
                >
                  View All Investor Notifications
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InboxDropdown;
